import { Outlet, NavLink, useLocation, useParams } from 'react-router-dom';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { Settings } from 'lucide-react';
import { GlobalCommand } from '@/components/GlobalCommand';
import { PWAInstallPrompt } from '@/components/layout/PWAInstallPrompt';
import { useLaadpleinDesign } from '../hooks/useLaadpleinDesign';
import { PRODUCT_NAME } from '../lib/branding';

/**
 * LpdLayout — sidebar+breadcrumb shell voor V2 list/detail pagina's.
 *
 * Identiek aan CRMLayout in styling/structuur, maar met dynamische breadcrumb
 * voor het V2-pad. Editor-routes (ScenarioEditor, KavelEditor, Compare) blijven
 * full-screen via een aparte route-tak in routes.tsx.
 */
export const LpdLayout = () => {
  const { userRole } = useAuth();
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { data: design } = useLaadpleinDesign(id);

  const isDetail = location.pathname.startsWith('/project-builder/') && !!id;
  const detailLabel = design?.name ?? 'Design';

  return (
    <SidebarProvider>
      <div className="flex h-svh w-full overflow-hidden">
        <AppSidebar />

        <SidebarInset className="flex flex-col h-full overflow-hidden">
          <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4 bg-background z-20">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem className="hidden md:block">
                  <BreadcrumbLink asChild>
                    <NavLink to="/crm" end>
                      CRM Dashboard
                    </NavLink>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="hidden md:block" />
                {isDetail ? (
                  <>
                    <BreadcrumbItem className="hidden md:block">
                      <BreadcrumbLink asChild>
                        <NavLink to="/project-builder">{PRODUCT_NAME}</NavLink>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator className="hidden md:block" />
                    <BreadcrumbItem>
                      <BreadcrumbPage>{detailLabel}</BreadcrumbPage>
                    </BreadcrumbItem>
                  </>
                ) : (
                  <BreadcrumbItem>
                    <BreadcrumbPage>{PRODUCT_NAME}</BreadcrumbPage>
                  </BreadcrumbItem>
                )}
              </BreadcrumbList>
            </Breadcrumb>

            <div className="ml-auto flex items-center gap-2">
              <GlobalCommand />
              {isAdmin && (
                <Button variant="ghost" size="sm" asChild>
                  <NavLink to="/crm/settings" className="flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    <span className="hidden sm:inline">Instellingen</span>
                  </NavLink>
                </Button>
              )}
            </div>
          </header>
          <main className="flex-1 overflow-auto overflow-x-hidden">
            <div className="p-3 sm:p-6 w-full max-w-full overflow-x-hidden">
              <Outlet />
            </div>
          </main>
        </SidebarInset>
      </div>
      <PWAInstallPrompt />
    </SidebarProvider>
  );
};

export default LpdLayout;
